import {
  ButtonGroup,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  Option,
  Separator,
  showNotification,
} from 'platform/components';
import {Grid, GridItem, HStack, VStack} from 'platform/foundation';

import {useEffect, useRef, useState} from 'react';

import {isNilOrEmpty, isString} from 'ramda-adjunct';

import {CustomerResponseBodyV2} from '@dms/api/customer';
import {CurrencyResponseBody} from '@dms/api/shared';
import {
  BaseSupplierOrder,
  GetSupplierOrderVariantsResponse,
  GetWarehouseResponse,
  GetWarehousesResponse,
  PatchSupplierOrderRequest,
  useLazyGetSupplierOrderTypeQuery,
  useLazyGetWarehouseQuery,
  usePatchSupplierOrderMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {
  getOptionsFromCurrencies,
  getOptionsFromCustomersAsSupplier,
  getOptionsFromWarehouses,
  handleApiError,
} from '@dms/shared';
import {
  getOptionsFromSupplierAccounts,
  getOptionsFromSupplierOrderTypes,
  getOptionsFromSupplierOrderVariant,
} from '@dms/warehouse/shared';

import {Nullish, TestIdProps, suffixTestId} from 'shared/web';

import {SupplierOrderForm, SupplierOrderFormSchema} from '../types/SupplierOrderForm';

export interface BasicInformationFormProps extends TestIdProps {
  supplierOrder: BaseSupplierOrder | Nullish;
  warehouses: GetWarehousesResponse | Nullish;
  suppliers: CustomerResponseBodyV2[] | Nullish;
  currencies: CurrencyResponseBody[] | Nullish;
  supplierOrderTypes: Option[] | Nullish;
  supplierAccounts: Option[] | Nullish;
  supplierOrderVariants: GetSupplierOrderVariantsResponse[] | Nullish;
  onSetReadonly: (isReadOnly: boolean) => void;
}

export function BasicInformationForm(props: BasicInformationFormProps) {
  const [selectedWarehouse, setSelectedWarehouse] = useState<GetWarehouseResponse | Nullish>(null);

  // Order type and supplier account options depend on the selected supplier. They are seeded once
  // from props and reloaded locally whenever the supplier changes. Seeding-once is
  // safe because GeneralInformationTab only mounts this form after the order's types/accounts have
  // loaded (its DataStatus gates on isSupplierOrderTypesLoading), so the initial value is populated.
  const [orderTypeOptions, setOrderTypeOptions] = useState<Option[]>(
    props.supplierOrderTypes ?? []
  );
  const [supplierAccountOptions, setSupplierAccountOptions] = useState<Option[]>(
    props.supplierAccounts ?? []
  );

  // Tracks the supplier whose order types and accounts are currently loaded. Seeded with the order's
  // supplier (its types/accounts come from props) and used both to discard out-of-order reload
  // responses and to skip reloading when the already-selected supplier is re-selected.
  const latestReloadSupplierIdRef = useRef<string | null>(props.supplierOrder?.supplierId ?? null);

  // Tracks whether the supplier has been changed in this edit
  // session. Until then a single-account field stays disabled with its value auto-filled on load;
  // once the supplier changes we force a conscious account re-pick, so the field is enabled for any
  // non-empty account list - even a single-account one (T20-128122).
  const [hasSupplierChanged, setHasSupplierChanged] = useState(false);

  // The warehouse is locked once the order is saved (TC1, T20-122890), so it is only fetched on
  // mount to drive the supplier filtering. The ref still guards against out-of-order responses if
  // the effect re-runs after the order is refetched with a different warehouse.
  const latestWarehouseIdRef = useRef<string | null>(props.supplierOrder?.warehouseId ?? null);

  const [patchSupplierOrder] = usePatchSupplierOrderMutation();
  const [getWarehouse, {isFetching: isWarehouseFetching}] = useLazyGetWarehouseQuery();
  const [getSupplierOrderType, {isFetching: isSupplierOrderTypeFetching}] =
    useLazyGetSupplierOrderTypeQuery();

  useEffect(
    function getWarehouseFromSupplierOrder() {
      const warehouseId = props.supplierOrder?.warehouseId;

      if (!isString(warehouseId)) {
        return;
      }

      latestWarehouseIdRef.current = warehouseId;

      getWarehouse({warehouseId})
        .unwrap()
        .then((warehouse) => {
          // Discard a stale response if the warehouse selection has since changed.
          if (latestWarehouseIdRef.current !== warehouseId) {
            return;
          }

          setSelectedWarehouse(warehouse);
        })
        .catch(handleApiError);
    },
    [props.supplierOrder?.warehouseId, getWarehouse]
  );

  // When the supplier has exactly one account the field is disabled (see below), so fall back
  // to auto-selecting it for orders that have none saved yet - otherwise the only available
  // account could never be persisted on save.
  const defaultSupplierAccountCode =
    props.supplierOrder?.supplierAccountCode ??
    (props.supplierAccounts?.length === 1 ? props.supplierAccounts[0].value : undefined);

  const defaultValues: Partial<SupplierOrderForm> = {
    warehouseId: props.supplierOrder?.warehouseId,
    supplierId: props.supplierOrder?.supplierId,
    orderVariantId: props.supplierOrder?.orderVariantId,
    type: props.supplierOrder?.type,
    supplierAccountCode: defaultSupplierAccountCode,
    currency: props.supplierOrder?.currency,
    exchangeRate: props.supplierOrder?.exchangeRate,
    ncConversion: props.supplierOrder?.ncConversion,
    note: props.supplierOrder?.note,
  };

  const handleDiscard = () => {
    props.onSetReadonly(true);
  };

  const handleSubmit: FormSubmitHandler<SupplierOrderForm> = async (data) => {
    const body: PatchSupplierOrderRequest['body'] = {
      warehouseId: data.warehouseId,
      supplierId: data.supplierId,
      orderVariantId: data.orderVariantId,
      type: data.type,
      supplierAccountCode: data.supplierAccountCode,
      currency: data.currency,
      exchangeRate: data.exchangeRate,
      ncConversion: data.ncConversion,
      orderExternalNumber: props.supplierOrder?.orderExternalNumber,
      referenceNumber: props.supplierOrder?.referenceNumber,
      note: data.note,
    };

    return await patchSupplierOrder({
      orderId: props.supplierOrder?.orderId ?? '',
      body,
    })
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('page.warehouse.notification.supplierOrderUpdated'));
        props.onSetReadonly(true);
      })
      .catch(handleApiError);
  };

  const getOptionsFromSuppliersByWarehouse = (suppliers: CustomerResponseBodyV2[] | Nullish) => {
    if (isNilOrEmpty(suppliers)) {
      return [];
    }

    const filteredSuppliers = suppliers?.filter((supplier) => {
      const warehouseSuppliers = selectedWarehouse?.suppliers?.map(
        (warehouseSupplier) => warehouseSupplier?.id
      );
      return warehouseSuppliers?.includes(supplier.id);
    });

    return getOptionsFromCustomersAsSupplier(filteredSuppliers);
  };

  return (
    <Form<SupplierOrderForm>
      experimentalZodSchema={SupplierOrderFormSchema}
      onSubmit={handleSubmit}
      defaultValues={defaultValues}
    >
      {(control, formApi) => {
        // Reload the order types and supplier accounts for the given supplier. The order variant is
        // locked, so it always comes from the existing order.
        const reloadOrderTypesAndAccounts = (supplierId: string | string[] | number | null) => {
          const orderVariantId = props.supplierOrder?.orderVariantId;

          // Changing the supplier always resets both the order
          // type and the account so the user re-picks them for the new supplier - even when only a
          // single account ends up available (T20-128122). The dropdown options are reloaded from the
          // backend below. Clear with `null` (not `undefined`): `undefined` leaves the controlled
          // Choice uncontrolled, so the previously selected value keeps showing in the browser. Do
          // not validate here: the form validates on submit, and eagerly erroring on a still-empty
          // field cannot be cleared until the next submit.
          setHasSupplierChanged(true);
          formApi.setValue('type', null, {shouldValidate: false});
          formApi.setValue('supplierAccountCode', null, {shouldValidate: false});

          if (!isString(supplierId) || !isString(orderVariantId)) {
            setOrderTypeOptions([]);
            setSupplierAccountOptions([]);
            return;
          }

          latestReloadSupplierIdRef.current = supplierId;

          getSupplierOrderType({supplierId, supplierOrderVariantId: orderVariantId})
            .unwrap()
            .then((response) => {
              // Discard responses for a supplier selection that has since been superseded by a
              // newer, still-pending reload (out-of-order responses on rapid supplier changes).
              if (latestReloadSupplierIdRef.current !== supplierId) {
                return;
              }

              setOrderTypeOptions(getOptionsFromSupplierOrderTypes(response.orderType));
              setSupplierAccountOptions(getOptionsFromSupplierAccounts(response.supplierAccount));
            })
            .catch(handleApiError);
        };

        const handleSupplierChange = (newSupplierId: string | string[] | number | null) => {
          // Re-selecting the already-loaded supplier would refetch the same dictionaries needlessly
          // (and must not reset the user's current order type / account selection).
          if (newSupplierId === latestReloadSupplierIdRef.current) {
            return;
          }

          reloadOrderTypesAndAccounts(newSupplierId);
        };

        // Before any change the single-account field stays disabled (auto-filled on load). Once the
        // supplier changes the account is cleared and the field is enabled for any non-empty account
        // list so the user can consciously re-pick one (T20-128122).
        const isSupplierAccountDisabled = hasSupplierChanged
          ? isNilOrEmpty(supplierAccountOptions)
          : supplierAccountOptions.length <= 1;

        return (
          <>
            <VStack spacing={4}>
              <Grid columns={4}>
                <FormField
                  isRequired
                  isDisabled
                  isNotClearable
                  control={control}
                  type="choice"
                  name="warehouseId"
                  label={i18n.t('entity.warehouse.labels.warehouse')}
                  options={getOptionsFromWarehouses(props.warehouses)}
                  data-testid={suffixTestId('inputs.warehouseId', props)}
                />
                <FormField
                  isRequired
                  isDisabled
                  isNotClearable
                  control={control}
                  type="choice"
                  name="orderVariantId"
                  label={i18n.t('entity.warehouse.labels.supplierOrderVariant')}
                  options={getOptionsFromSupplierOrderVariant(props.supplierOrderVariants)}
                  data-testid={suffixTestId('inputs.orderVariantId', props)}
                />
                <FormField
                  isRequired
                  isNotClearable
                  isDisabled={isWarehouseFetching}
                  isLoading={isWarehouseFetching}
                  control={control}
                  onChange={handleSupplierChange}
                  type="choice"
                  name="supplierId"
                  label={i18n.t('entity.warehouse.labels.supplier')}
                  options={getOptionsFromSuppliersByWarehouse(props.suppliers)}
                  data-testid={suffixTestId('inputs.supplierId', props)}
                />
                <FormField
                  control={control}
                  type="choice"
                  name="type"
                  label={i18n.t('entity.warehouse.labels.supplierOrderType')}
                  options={orderTypeOptions}
                  isLoading={isSupplierOrderTypeFetching}
                  data-testid={suffixTestId('inputs.type', props)}
                />
                <FormField
                  isNotClearable
                  control={control}
                  type="choice"
                  name="supplierAccountCode"
                  label={i18n.t('entity.warehouse.labels.supplierAccount')}
                  options={supplierAccountOptions}
                  isDisabled={isSupplierAccountDisabled}
                  isLoading={isSupplierOrderTypeFetching}
                  data-testid={suffixTestId('inputs.supplierAccountCode', props)}
                />
              </Grid>
              <Grid columns={4}>
                <FormField
                  control={control}
                  type="choice"
                  name="currency"
                  label={i18n.t('entity.warehouse.labels.currency')}
                  options={getOptionsFromCurrencies(props.currencies)}
                  data-testid={suffixTestId('inputs.currency', props)}
                />
                <FormField
                  control={control}
                  type="number"
                  name="exchangeRate"
                  label={i18n.t('entity.warehouse.labels.exchangeRate')}
                  data-testid={suffixTestId('inputs.exchangeRate', props)}
                />
                <FormField
                  control={control}
                  type="choice"
                  name="ncConversion"
                  label={i18n.t('entity.warehouse.labels.ncConversion')}
                  options={[]}
                  isDisabled
                  data-testid={suffixTestId('inputs.ncConversion', props)}
                />
              </Grid>
              <Grid columns={4}>
                <GridItem span={4}>
                  <FormField
                    control={control}
                    type="textarea"
                    name="note"
                    label={i18n.t('entity.warehouse.labels.note')}
                    data-testid={suffixTestId('inputs.note', props)}
                  />
                </GridItem>
              </Grid>
            </VStack>
            <Separator />
            <HStack justify="flex-end">
              <ButtonGroup>
                <FormButton
                  control={control}
                  type="reset"
                  variant="secondary"
                  title={i18n.t('general.labels.discard')}
                  onClick={handleDiscard}
                  data-testid={suffixTestId('actions.discard', props)}
                />
                <FormButton
                  control={control}
                  type="submit"
                  variant="primary"
                  title={i18n.t('general.labels.saveChanges')}
                  data-testid={suffixTestId('actions.submit', props)}
                />
              </ButtonGroup>
            </HStack>
          </>
        );
      }}
    </Form>
  );
}
